import React from 'react'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { Pie } from 'react-chartjs-2'
import { TeamCostDistributionData, reportService } from '@/services/reportService'

ChartJS.register(ArcElement, Tooltip, Legend)

interface TeamCostDistributionChartProps {
  data: TeamCostDistributionData[]
  loading: boolean
}

// 生成不同的顏色
const generateColors = (count: number): string[] => {
  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
    '#14B8A6', '#F43F5E', '#8B5A2B', '#059669', '#DC2626'
  ]
  
  const result = []
  for (let i = 0; i < count; i++) {
    result.push(colors[i % colors.length])
  }
  return result
}

const TeamCostDistributionChart: React.FC<TeamCostDistributionChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="card p-6">
        <h3 className="text-xl font-semibold text-secondary-800 mb-4">團隊成員花費分佈</h3>
        <div className="h-80 bg-secondary-100 rounded animate-pulse flex items-center justify-center">
          <div className="text-secondary-500">載入中...</div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="card p-6">
        <h3 className="text-xl font-semibold text-secondary-800 mb-4">團隊成員花費分佈</h3>
        <div className="h-80 flex items-center justify-center text-secondary-500">
          <div className="text-center">
            <div className="text-4xl mb-2">👥</div>
            <p>暫無團隊數據</p>
          </div>
        </div>
      </div>
    )
  }

  const colors = generateColors(data.length)
  
  const chartData = {
    labels: data.map(item => item.userName),
    datasets: [
      {
        data: data.map(item => item.cost),
        backgroundColor: colors.map(color => color + '80'), // 添加透明度
        borderColor: colors,
        borderWidth: 2,
        hoverBorderWidth: 3,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          generateLabels: function(chart: any) {
            const data = chart.data
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label: string, i: number) => {
                const dataset = data.datasets[0]
                const cost = dataset.data[i]
                const percentage = ((cost / dataset.data.reduce((a: number, b: number) => a + b, 0)) * 100).toFixed(1)
                
                return {
                  text: `${label} (${reportService.formatCurrency(cost)}, ${percentage}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor[i],
                  lineWidth: dataset.borderWidth,
                  hidden: false,
                  index: i
                }
              })
            }
            return []
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || ''
            const value = context.parsed
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0)
            const percentage = ((value / total) * 100).toFixed(1)
            
            return `${label}: ${reportService.formatCurrency(value)} (${percentage}%)`
          },
          afterLabel: function(context: any) {
            const dataIndex = context.dataIndex
            const memberData = data[dataIndex]
            return [
              `工時: ${memberData.hours.toFixed(1)} 小時`,
              `時薪: ${reportService.formatCurrency(memberData.hourlyRate)}`
            ]
          }
        }
      }
    },
  }

  const totalCost = data.reduce((sum, item) => sum + item.cost, 0)
  const totalHours = data.reduce((sum, item) => sum + item.hours, 0)
  const averageHourlyRate = data.length > 0 ? 
    data.reduce((sum, item) => sum + item.hourlyRate, 0) / data.length : 0

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-secondary-800">團隊成員花費分佈</h3>
        <div className="text-sm text-secondary-600">
          總花費: {reportService.formatCurrency(totalCost)}
        </div>
      </div>
      
      <div className="h-80">
        <Pie data={chartData} options={options} />
      </div>
      
      {/* 成員詳細列表 */}
      <div className="mt-6">
        <h4 className="text-lg font-medium text-secondary-800 mb-3">成員詳情</h4>
        <div className="space-y-2">
          {data.map((item, index) => (
            <div key={item.userId} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: colors[index] }}
                />
                <span className="font-medium text-secondary-800">{item.userName}</span>
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <span className="text-secondary-600">
                  {item.hours.toFixed(1)}h
                </span>
                <span className="text-secondary-600">
                  {reportService.formatCurrency(item.hourlyRate)}/h
                </span>
                <span className="font-medium text-primary-600">
                  {reportService.formatCurrency(item.cost)}
                </span>
                <span className="text-xs text-secondary-500">
                  ({item.percentage.toFixed(1)}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* 統計摘要 */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="text-lg font-semibold text-blue-600">{data.length}</div>
          <div className="text-sm text-blue-500">團隊成員</div>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="text-lg font-semibold text-green-600">
            {totalHours.toFixed(1)}h
          </div>
          <div className="text-sm text-green-500">總工時</div>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg">
          <div className="text-lg font-semibold text-purple-600">
            {reportService.formatCurrency(averageHourlyRate)}
          </div>
          <div className="text-sm text-purple-500">平均時薪</div>
        </div>
        <div className="p-3 bg-orange-50 rounded-lg">
          <div className="text-lg font-semibold text-orange-600">
            {data.length > 0 ? Math.max(...data.map(item => item.cost)) : 0}
          </div>
          <div className="text-sm text-orange-500">最高花費</div>
        </div>
      </div>
    </div>
  )
}

export default TeamCostDistributionChart
